'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import Script from 'next/script'
import { useGetUserSettingsQuery } from '@/redux/api/userSettingApi'

interface FacebookContextType {
  isSDKReady: boolean
  appId: string | null
}

const FacebookContext = createContext<FacebookContextType>({
  isSDKReady: false,
  appId: null,
})

export const useFacebookSDK = () => useContext(FacebookContext)

export const FacebookSDKProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: userSettingsData } = useGetUserSettingsQuery({})
  const [isSDKReady, setIsSDKReady] = useState(false)
  const [appId, setAppId] = useState<string | null>(null)

  useEffect(() => {
    const facebookAppId = userSettingsData?.data?.facebook_app_id
    if (facebookAppId) {
      setAppId(facebookAppId)
      
      if (typeof window !== 'undefined') {
        // Define fbAsyncInit as early as possible
        window.fbAsyncInit = function() {
          if (!window.FB) return;
          window.FB.init({
            appId: facebookAppId,
            cookie: true,
            xfbml: true,
            version: userSettingsData?.data?.facebook_api_version || 'v18.0'
          })
          setIsSDKReady(true)
        }

        // If FB is already loaded, initialize it manually
        if (window.FB) {
          window.FB.init({
            appId: facebookAppId,
            cookie: true,
            xfbml: true,
            version: userSettingsData?.data?.facebook_api_version || 'v18.0'
          })
          setIsSDKReady(true)
        }
      }
    }
  }, [userSettingsData])

  return (
    <FacebookContext.Provider value={{ isSDKReady, appId }}>
      {appId && (
        <Script
          id="facebook-sdk"
          src="https://connect.facebook.net/en_US/sdk.js"
          strategy="afterInteractive"
          onLoad={() => {
            // Manual check if fbAsyncInit didn't fire
            if (window.FB && !isSDKReady) {
              const facebookAppId = userSettingsData?.data?.facebook_app_id
              if (facebookAppId) {
                window.FB.init({
                  appId: facebookAppId,
                  cookie: true,
                  xfbml: true,
                  version: userSettingsData?.data?.facebook_api_version || 'v18.0'
                })
                setIsSDKReady(true)
              }
            }
          }}
        />
      )}
      {children}
    </FacebookContext.Provider>
  )
}

declare global {
  interface Window {
    FB: any
    fbAsyncInit: () => void
  }
}
